import { cn } from '@/lib/utils';
import { Icon, Typography } from '@/shared-ui/atoms';
import type { ComponentProps, ComponentPropsWithoutRef, ComponentType, SVGProps } from 'react';

const Root = ({ className, ...props }: ComponentProps<'div'>) => {
    return <div data-slot="logo-stack" className={cn('flex flex-col gap-4', className)} {...props} />;
};

Root.displayName = 'LogoStack.Root';

const Title = ({ children, className, ...props }: ComponentProps<'h4'>) => {
    return (
        <Typography variant="h4" className={cn('font-normal', className)} {...props}>
            {children}
        </Typography>
    );
};
Title.displayName = 'LogoStack.Title';

const Wrapper = ({ className, children, ...props }: ComponentProps<'div'>) => {
    return (
        <div className={cn('flex flex-row gap-2', className)} {...props}>
            {children}
        </div>
    );
};

Wrapper.displayName = 'LogoStack.Wrapper';

const Label = ({ children, className, ...props }: ComponentPropsWithoutRef<'span'>) => {
    return (
        <Typography
            variant="span"
            className={cn('text-base font-light shrink-0 leading-8 text-primary-hover', className)}
            {...props}>
            {children}
        </Typography>
    );
};
Label.displayName = 'LogoStack.Label';

type LogoStackSvgLogo = {
    id?: string;
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    alt: string;
};

export type LogoStackLogo = LogoStackSvgLogo;

type LogoRowProps = Omit<ComponentProps<'div'>, 'children'> & {
    logos: LogoStackLogo[];
    logoClassName?: string;
};

const LogoRow = ({ className, logos, logoClassName, ...props }: LogoRowProps) => {
    return (
        <div data-slot="logo-stack-row" className={cn('flex flex-row flex-wrap gap-2', className)} {...props}>
            {logos.map((logo) => {
                return (
                    <Icon
                        icon={logo.Icon}
                        key={logo.id ?? logo.alt}
                        className={cn('h-8 w-auto', logoClassName)}
                        decorative={false}
                        title={logo.alt}
                    />
                );
            })}
        </div>
    );
};
LogoRow.displayName = 'LogoStack.LogoRow';

const LogoStack = { Root, Title, Label, Wrapper, LogoRow } as const;
export { LogoStack };
